import React, { useState, useEffect } from 'react';
import { ArrowRight, ArrowLeft, Copy, Check, Rocket, ExternalLink, RefreshCw, Zap, Link as LinkIcon, Filter, Search, Settings, ToggleLeft, ToggleRight, PlayCircle, BookOpen, Terminal, AlertTriangle, Shield, PenTool, Image as ImageIcon, Video, Globe, Smartphone, LayoutDashboard, Send, Home, Monitor } from 'lucide-react';
import { SimulatedSheet, SimulatedEditor, SimulatedDeploy, SimulatedAuth } from './SimulatedUI';
import { Step, ScriptConfig, generateScript } from '../../../types';
import { useAcademyStore } from '../../../store/academyStore';
import { ModuleSuccess } from '../../ui/ModuleSuccess';

// In a full refactor, all wizards would receive onComplete
interface StepWizardProps {
    onBack?: () => void;
    // onComplete?: () => void; // Added for future extension
}

const steps: Step[] = [
    {
        id: 1,
        title: "Start Your Spreadsheet",
        description: "Open a new Sheet. \n\n**The Golden Rule:** Row 1 is for *Headers* (Labels like 'Name', 'ImageURL', 'Price'). Row 2+ is for your *Data*. \n\nAvoid merging cells or adding titles above Row 1!",
        actionItem: 'open-sheets',
        buttonText: "Create New Sheet"
    },
    {
        id: 2,
        title: "Open the Script Editor",
        description: "Look at the top menu. Click 'Extensions', then click 'Apps Script'. A new tab will open with code.",
        actionItem: 'manual-check',
        buttonText: "I've Opened The Editor"
    },
    {
        id: 3,
        title: "Build Your API",
        description: "Choose your superpowers! Toggle the features you need, and we'll write the code for you instantly.",
        actionItem: 'configure-script',
        buttonText: "Generate & Copy Code"
    },
    {
        id: 4,
        title: "The System Code",
        description: "Delete any code you see in the editor. Copy the code below and paste it there. This code teaches your sheet how to speak JSON!",
        actionItem: 'copy-code',
        buttonText: "Copy Code"
    },
    {
        id: 5,
        title: "Initial Save",
        description: "Before running anything, we must save. Click the disk icon 💾 or press Ctrl+S. Name your project 'API v1' if asked.",
        actionItem: 'manual-check',
        buttonText: "Project Saved"
    },
    {
        id: 6,
        title: "Authorization (The Tricky Part)",
        description: "Click 'Run' ▶️. A window will pop up. Click 'Review Permissions' -> Choose Account -> 'Advanced' -> 'Go to (unsafe)'. This is normal for your own scripts!",
        actionItem: 'manual-check',
        buttonText: "Permissions Granted"
    },
    {
        id: 7,
        title: "Deploy Your App",
        description: "Click the blue 'Deploy' button (top right) -> 'New Deployment'. Select 'Web App' as the type (click the gear icon if needed).",
        actionItem: 'manual-check',
        buttonText: "I Clicked Deploy"
    },
    {
        id: 8,
        title: "Access Control (Crucial!)",
        description: "Set 'Execute as' to 'Me'. \nSet 'Who has access' to 'Anyone'. \n\nIf you miss this, your API will be private and the app won't work!",
        actionItem: 'manual-check',
        buttonText: "Settings Updated"
    },
    {
        id: 9,
        title: "Finalize Deployment",
        description: "Click 'Deploy'. Copy the 'Web App URL' it gives you. It should start with `https://script.google.com/macros/s/...`",
        actionItem: 'manual-check',
        buttonText: "I Have The URL"
    },
    {
        id: 10,
        title: "Test connection",
        description: "Paste your URL below to verify the connection. If it works, you'll see your spreadsheet data appear magically!",
        actionItem: 'test-url'
    }
];

export const StepWizard = ({ onBack }: StepWizardProps) => {
    const { completeModule } = useAcademyStore();
    const [currentStep, setCurrentStep] = useState(1);
    const [copied, setCopied] = useState(false);
    const [testUrl, setTestUrl] = useState('');
    const [testResult, setTestResult] = useState<null | any>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [isIdle, setIsIdle] = useState(false);
    const [completed, setCompleted] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);

    // Script Configuration State
    const [config, setConfig] = useState<ScriptConfig>({
        filtering: true,
        search: true,
        caseSensitive: false,
        allowWrite: false,
        securityToken: ''
    });
    const [generatedCode, setGeneratedCode] = useState('');

    // Idle Timer Effect
    useEffect(() => {
        setIsIdle(false);
        const timer = setTimeout(() => {
            setIsIdle(true);
        }, 10000); // Highlight after 10s of inactivity
        return () => clearTimeout(timer);
    }, [currentStep, testUrl, config, copied]);

    useEffect(() => {
        setGeneratedCode(generateScript(config));
    }, [config]);

    // Complete the module using the store
    const handleModuleComplete = () => {
        if (!completed) {
            setCompleted(true);
            completeModule('spreadsheet-service', 100);
        }
    };

    const handleNext = () => {
        if (currentStep < steps.length) {
            setTimeout(() => setCurrentStep(c => c + 1), 200);
        } else {
            // Completed!
            handleModuleComplete();
            setShowSuccess(true);
        }
    };

    const handlePrev = () => {
        if (currentStep > 1) setCurrentStep(c => c - 1);
    };

    const handleOpenSheets = () => {
        window.open('https://sheets.new', '_blank');
        handleNext();
    };

    const copyCode = () => {
        navigator.clipboard.writeText(generatedCode);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        handleNext();
    };

    const runTest = async () => {
        if (!testUrl) return;

        // Validate URL format
        if (!testUrl.trim().startsWith('https://script.google.com')) {
            setError("Invalid URL! Please copy the FULL URL starting with 'https://script.google.com'.");
            return;
        }

        setLoading(true);
        setError('');
        setTestResult(null);

        // Append token if exists in config
        let finalUrl = testUrl.trim();
        if (config.securityToken && !testUrl.includes('token=')) {
            finalUrl += (finalUrl.includes('?') ? '&' : '?') + `token=${config.securityToken}`;
        }

        try {
            // Use 'no-cors' ? No, we need body.
            // Google Apps Script redirects. fetch follows automatically.
            const response = await fetch(finalUrl, { method: 'GET' });

            const contentType = response.headers.get("content-type");
            if (!contentType || !contentType.includes("application/json")) {
                // Check if it's the specific "Hello" text from the old code
                const text = await response.text();
                if (text.includes("Hello from Gorilla")) {
                    throw new Error("You are running the OLD code! Please go back to Step 3, generate the new script, and deploy a 'New Version'.");
                }
                throw new Error("Connection failed. \n1. Did you set 'Who has access' to 'Anyone'?\n2. Did you click 'New Version' when deploying?");
            }
            const data = await response.json();
            if (data.error) throw new Error(data.error);
            setTestResult(data);
        } catch (err: any) {
            console.error(err);
            setError(err.message || "Network Error. Check your URL and Internet.");
        } finally {
            setLoading(false);
        }
    };

    const renderVisual = (stepId: number) => {
        switch (stepId) {
            case 1: return <SimulatedSheet />;
            case 2: return <div className="text-center p-10 bg-gray-100 rounded-lg"><p className="text-xl font-bold text-gray-500">Extensions &gt; Apps Script</p></div>;
            case 3:
            case 4: return <SimulatedEditor code={generatedCode} />;
            case 5: return <SimulatedAuth />;
            case 6:
            case 7: return <SimulatedDeploy />;
            case 8:
                if (testResult) {
                    return (
                        <div className="w-full h-full p-2 animate-fade-in-up">
                            <div className="bg-slate-900 rounded-2xl p-6 h-full overflow-auto border-2 border-slate-700 shadow-2xl text-left">
                                <div className="flex justify-between items-center mb-4 border-b border-slate-700 pb-2">
                                    <span className="text-green-400 font-bold font-mono text-sm">HTTP 200 OK</span>
                                    <span className="text-slate-500 text-xs">JSON</span>
                                </div>
                                <pre className="font-mono text-sm text-green-300 leading-relaxed">
                                    {JSON.stringify(testResult, null, 2)}
                                </pre>
                            </div>
                        </div>
                    );
                }
                return <div className="flex justify-center text-9xl animate-bounce">🎉</div>;
            default: return <div className="flex justify-center text-9xl">🎉</div>;
        }
    };

    const activeStep = steps.find(s => s.id === currentStep)!;
    const pulseClass = isIdle ? "ring-4 ring-yellow-300 shadow-xl animate-pulse transform scale-105" : "";

    return (
        <div
            className="max-w-6xl mx-auto w-full px-4 py-8 min-h-full flex flex-col relative"
            style={{
                background: `linear-gradient(to bottom, rgba(255, 255, 255, 0.96), rgba(240, 246, 255, 0.98)), url('/assets/sheet-wizard-bg.png')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'fixed'
            }}
        >
            <div className="mb-4 flex items-center">
                {onBack && (
                    <button onClick={onBack} className="text-gray-500 hover:text-google-blue flex items-center gap-1 font-bold text-sm">
                        <Home size={16} /> Back to Dashboard
                    </button>
                )}
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-3 mb-8 shrink-0">
                <div
                    className="bg-google-blue h-3 rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${(currentStep / steps.length) * 100}%` }}
                />
            </div>

            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
                {/* Left Side: Instructions */}
                <div className="space-y-6 animate-fade-in-up">
                    <div className="inline-block px-3 py-1 bg-blue-100 text-blue-700 font-bold rounded-full text-sm">
                        Step {currentStep} of {steps.length}
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-800 font-comic">
                        {activeStep.title}
                    </h2>
                    <div className="text-lg text-gray-600 leading-relaxed whitespace-pre-wrap">
                        {activeStep.description}
                    </div>

                    {currentStep === 1 && (
                        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg shadow-sm">
                            <h4 className="font-bold text-yellow-800 mb-2">Structure Guide:</h4>
                            <ul className="list-disc list-inside text-sm text-yellow-900 space-y-1">
                                <li><strong className="text-black">Row 1:</strong> Headers only! (e.g., Name, ID, Score). No empty columns.</li>
                                <li><strong className="text-black">Row 2+:</strong> Your actual data.</li>
                                <li><strong className="text-black">Images/Video:</strong> Paste the full URL (https://...) into the cell.</li>
                            </ul>
                        </div>
                    )}

                    {/* Action Area */}
                    <div className="mt-6">
                        {activeStep.actionItem === 'open-sheets' && (
                            <button
                                onClick={handleOpenSheets}
                                className={`inline-flex items-center gap-2 bg-google-green text-white px-6 py-4 rounded-xl font-bold shadow-lg hover:bg-green-600 transition-all hover:scale-105 active:scale-95 w-full sm:w-auto justify-center ${pulseClass}`}
                            >
                                Create New Sheet <ExternalLink size={20} />
                            </button>
                        )}

                        {activeStep.actionItem === 'manual-check' && (
                            <button
                                onClick={handleNext}
                                className={`inline-flex items-center gap-2 bg-white border-2 border-google-blue text-google-blue px-6 py-4 rounded-xl font-bold shadow-sm hover:bg-blue-50 transition-all active:scale-95 w-full sm:w-auto justify-center ${pulseClass}`}
                            >
                                {activeStep.buttonText || "I Did It, Next!"} <ArrowRight size={20} />
                            </button>
                        )}

                        {activeStep.actionItem === 'configure-script' && (
                            <div className="space-y-4">
                                <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                                    onClick={() => setConfig(c => ({ ...c, filtering: !c.filtering }))}>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className={`p-2 rounded-lg ${config.filtering ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                                                <Filter size={20} />
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-gray-800">Smart Filtering</h4>
                                                <p className="text-xs text-gray-500">Filter data by column (e.g. ?Category=Tech)</p>
                                            </div>
                                        </div>
                                        {config.filtering ? <ToggleRight size={32} className="text-google-green" /> : <ToggleLeft size={32} className="text-gray-300" />}
                                    </div>
                                </div>

                                <button
                                    onClick={handleNext}
                                    className={`w-full inline-flex items-center justify-center gap-2 bg-google-blue text-white px-6 py-4 rounded-xl font-bold shadow-lg hover:bg-blue-600 transition-all hover:scale-105 active:scale-95 mt-4 ${pulseClass}`}
                                >
                                    Generate Script <Rocket size={20} />
                                </button>
                            </div>
                        )}

                        {activeStep.actionItem === 'copy-code' && (
                            <div className="relative group">
                                <div className="absolute top-0 right-0 p-2 z-10 flex gap-2">
                                    <button
                                        onClick={copyCode}
                                        className={`flex items-center gap-2 bg-google-blue text-white px-4 py-2 rounded-lg font-bold shadow-lg hover:bg-blue-600 transition-all active:scale-95 text-sm ${pulseClass}`}
                                    >
                                        {copied ? <Check size={16} /> : <Copy size={16} />}
                                        {copied ? "Copied!" : "Copy Code & Continue"}
                                    </button>
                                </div>
                                <pre className="bg-[#1e1e1e] text-blue-300 p-4 pt-12 rounded-xl overflow-auto max-h-80 text-sm shadow-inner border border-gray-700 font-mono leading-relaxed">
                                    <code>{generatedCode}</code>
                                </pre>
                            </div>
                        )}

                        {activeStep.actionItem === 'test-url' && (
                            <div className="space-y-6">
                                <div className="bg-white p-5 rounded-xl shadow-md border border-gray-200 space-y-4">
                                    <label className="block text-sm font-bold text-gray-700">1. Paste your Web App URL here to test:</label>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={testUrl}
                                            onChange={(e) => setTestUrl(e.target.value)}
                                            placeholder="https://script.google.com/macros/s/..."
                                            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-google-blue focus:border-transparent outline-none text-sm font-mono text-gray-600"
                                        />
                                        <button
                                            onClick={runTest}
                                            disabled={loading || !testUrl}
                                            className={`bg-google-blue text-white px-4 py-2 rounded-lg font-bold hover:bg-blue-600 disabled:opacity-50 flex items-center gap-2 ${pulseClass}`}
                                        >
                                            {loading ? <RefreshCw className="animate-spin" size={18} /> : <Rocket size={18} />}
                                            Test
                                        </button>
                                    </div>
                                    {error && (
                                        <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm flex items-start gap-2 animate-fade-in-up border border-red-200">
                                            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                                            <div>
                                                <div className="font-bold">Test Failed</div>
                                                <div>{error}</div>
                                            </div>
                                        </div>
                                    )}
                                    {testResult && (
                                        <div className="animate-fade-in-up mt-4 bg-green-50 border border-green-200 p-4 rounded-xl">
                                            <p className="text-green-700 font-bold text-lg mb-1 flex items-center gap-2">
                                                <Check size={24} /> Connection Successful!
                                            </p>
                                            <p className="text-green-600 text-sm hidden md:block">
                                                Look at the right panel to see your live data! 👉
                                            </p>
                                            <p className="text-green-600 text-sm md:hidden">
                                                Scroll down to see your data! 👇
                                            </p>
                                        </div>
                                    )}
                                </div>

                                <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-xl p-6 text-white shadow-lg animate-fade-in-up">
                                    <h3 className="text-xl font-bold font-comic mb-6 flex items-center gap-2">
                                        <Rocket className="animate-pulse" /> What can you build?
                                    </h3>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div className="bg-white/10 p-4 rounded-lg backdrop-blur-sm border border-white/20 hover:bg-white/20 transition-colors">
                                            <div className="flex items-center gap-2 mb-2 font-bold text-indigo-100">
                                                <Monitor size={16} /> Google Sites (Easiest)
                                            </div>
                                            <p className="text-xs text-indigo-100 leading-relaxed">
                                                Use Google Sites to drag-and-drop a beautiful website. It can display your sheet data without writing any code at all!
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Side: Visual Aid */}
                <div className="hidden md:flex flex-col items-center justify-center h-full bg-white rounded-3xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.05)] border border-slate-100 relative overflow-hidden">
                    <div className="absolute top-4 right-4 text-slate-300 font-comic font-bold text-6xl opacity-20 select-none">
                        #{currentStep}
                    </div>
                    <div className="w-full transform transition-transform duration-500 hover:scale-[1.02]">
                        {renderVisual(currentStep)}
                    </div>
                    <p className="mt-6 text-sm text-gray-400 font-medium italic">
                        (This is just a picture of what yours should look like!)
                    </p>
                </div>
            </div>

            <div className="mt-auto pt-8 flex justify-between items-center border-t border-gray-200 shrink-0">
                <button
                    onClick={handlePrev}
                    disabled={currentStep === 1}
                    className={`flex items-center gap-2 font-bold px-6 py-3 rounded-xl transition-all ${currentStep === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-100'
                        }`}
                >
                    <ArrowLeft size={20} /> Back
                </button>

                <button
                    onClick={handleNext}
                    className={`flex items-center gap-2 font-bold px-8 py-3 rounded-xl shadow-lg transform transition-all active:scale-95 ${currentStep === steps.length
                        ? 'bg-green-500 text-white hover:bg-green-600'
                        : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'
                        }`}
                >
                    {currentStep === steps.length ? "Finish Module" : "Skip Step"} <ArrowRight size={20} />
                </button>
            </div>
            {showSuccess && (
                <ModuleSuccess
                    title="Spreadsheet API"
                    onRetry={() => {
                        setCurrentStep(1);
                        setShowSuccess(false);
                    }}
                    children={onBack ? undefined : (
                        <div className="bg-blue-50 p-4 rounded-xl text-left">
                            <h4 className="font-bold text-blue-800 mb-2">🚀 Next Steps:</h4>
                            <p className="text-blue-700 text-sm">
                                You now have a live API! You can use this URL in your own apps, websites, or sharing it with friends.
                            </p>
                        </div>
                    )}
                />
            )}

            {/* Educational Content Section */}
            <div className="mt-12 space-y-8 pb-12 animate-fade-in-up">

                {/* Theory Section */}
                <div className="bg-white p-8 rounded-3xl shadow-lg border border-gray-200">
                    <h2 className="text-2xl font-black text-gray-800 mb-6 flex items-center gap-3">
                        📚 How Did This Work?
                    </h2>

                    <div className="grid md:grid-cols-2 gap-8">
                        <div>
                            <h3 className="font-bold text-lg text-google-blue mb-3">Spreadsheet as a Database</h3>
                            <p className="text-gray-600 mb-4">
                                Most databases cost money. Google Sheets is free!
                                But apps can't "read" a visual spreadsheet. They need raw data.
                            </p>

                            <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 mt-4">
                                <h4 className="font-bold text-gray-700 mb-2 flex items-center gap-2">
                                    <Terminal size={16} /> JSON: The Universal Language
                                </h4>
                                <p className="text-gray-500 text-xs mb-2">We converted your rows into <strong>JSON</strong> (JavaScript Object Notation):</p>
                                <div className="font-mono text-xs bg-gray-800 text-green-400 p-3 rounded-lg">
                                    <div>[</div>
                                    <div className="pl-4">{"{"} "Name": "Pikachu", "Power": "Lightning" {"}"},</div>
                                    <div className="pl-4">{"{"} "Name": "Mario", "Power": "Jump" {"}"}</div>
                                    <div>]</div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-blue-50 p-6 rounded-xl border border-blue-100">
                            <h3 className="font-bold text-lg text-blue-900 mb-3">☁️ Serverless Functions</h3>
                            <p className="text-blue-800 text-sm mb-4">
                                When you deployed the script, you created a <strong>Serverless Function</strong> (Google Apps Script).
                            </p>

                            <div className="space-y-3">
                                <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 bg-white text-blue-600 rounded-lg flex items-center justify-center shrink-0 shadow-sm font-bold">1</div>
                                    <div>
                                        <h4 className="font-bold text-blue-900 text-sm">Request</h4>
                                        <p className="text-blue-700 text-xs">Your website calls the URL (sends a GET request).</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 bg-white text-blue-600 rounded-lg flex items-center justify-center shrink-0 shadow-sm font-bold">2</div>
                                    <div>
                                        <h4 className="font-bold text-blue-900 text-sm">Wake Up</h4>
                                        <p className="text-blue-700 text-xs">Google wakes up a tiny server, runs your code, and shuts it down.</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 bg-white text-blue-600 rounded-lg flex items-center justify-center shrink-0 shadow-sm font-bold">3</div>
                                    <div>
                                        <h4 className="font-bold text-blue-900 text-sm">Response</h4>
                                        <p className="text-blue-700 text-xs">The server sends back the JSON data.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Limitations */}
                <div className="bg-yellow-50 p-6 rounded-xl border border-yellow-200">
                    <h3 className="font-bold text-yellow-800 mb-2 flex items-center gap-2">
                        <Rocket size={18} /> Production Ready?
                    </h3>
                    <p className="text-yellow-900 text-sm mb-4">
                        This is amazing for prototypes and small apps! But be careful:
                    </p>
                    <div className="grid md:grid-cols-3 gap-4">
                        <div className="bg-white p-3 rounded-lg border border-yellow-100">
                            <span className="font-bold text-yellow-700 text-xs uppercase">Speed</span>
                            <p className="text-gray-600 text-sm">It's slow. Takes ~1-2 seconds per request.</p>
                        </div>
                        <div className="bg-white p-3 rounded-lg border border-yellow-100">
                            <span className="font-bold text-yellow-700 text-xs uppercase">Privacy</span>
                            <p className="text-gray-600 text-sm">Your data is public if you set access to "Anyone".</p>
                        </div>
                        <div className="bg-white p-3 rounded-lg border border-yellow-100">
                            <span className="font-bold text-yellow-700 text-xs uppercase">Limits</span>
                            <p className="text-gray-600 text-sm">Google allows ~20,000 requests per day for free.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
